import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link, Routes, Route, NavLink } from "react-router-dom";
import { api } from "@/lib/api";
import { formatCAD } from "@/lib/format";
import {
  LayoutDashboard, Package, ShoppingBag, Users, Star, MessageSquare,
  Tag, BarChart3, Settings, Lock, Box, Mail, MessageCircle, Receipt, Briefcase
} from "lucide-react";

const SCAFFOLDED = [
  { icon: Mail, label: "Email Marketing" },
  { icon: MessageCircle, label: "SMS Marketing" },
  { icon: Users, label: "CRM" },
  { icon: Receipt, label: "Invoices" },
  { icon: Briefcase, label: "POS" },
  { icon: Star, label: "Loyalty" },
];

const STATUS_COLOR = {
  PENDING: "bg-[#EFECE6] text-[#5C5A56]",
  PAID: "bg-[#E5EBE8] text-[#2A4B3A]",
  PROCESSING: "bg-[#E5EBE8] text-[#2A4B3A]",
  SHIPPED: "bg-[#F2E8E9] text-[#702A32]",
  DELIVERED: "bg-[#E5EBE8] text-[#2A4B3A]",
  CANCELLED: "bg-[#FBE9E9] text-[#B73B3B]",
};

export default function MerchantDashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  useEffect(() => {
    if (!loading && (!user || (user.role !== "merchant" && user.role !== "admin"))) nav("/login");
  }, [user, loading, nav]);
  if (!user) return null;

  return (
    <div className="min-h-screen flex bg-[#F3F2F0]" data-testid="merchant-dashboard">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-[#E5E3DD] hidden md:flex flex-col">
        <Link to="/" className="px-6 py-5 border-b border-[#E5E3DD] flex items-center gap-2">
          <div className="w-7 h-7 bg-[#702A32] flex items-center justify-center"><span className="font-heading text-white text-base">M</span></div>
          <span className="font-heading text-lg">MapleMarket</span>
        </Link>
        <nav className="flex-1 py-4 px-3 space-y-0.5">
          <NavItem to="/merchant" icon={LayoutDashboard} label="Overview" end />
          <NavItem to="/merchant/products" icon={Package} label="Products" />
          <NavItem to="/merchant/orders" icon={ShoppingBag} label="Orders" />
          <NavItem to="/merchant/customers" icon={Users} label="Customers" />
          <NavItem to="/merchant/coupons" icon={Tag} label="Coupons" />
          <NavItem to="/merchant/analytics" icon={BarChart3} label="Analytics" />
          <NavItem to="/merchant/reviews" icon={Star} label="Reviews" />
          <NavItem to="/merchant/messages" icon={MessageSquare} label="Messages" />
          <NavItem to="/merchant/settings" icon={Settings} label="Store settings" />
          <div className="overline px-3 mt-6 mb-2">Coming soon</div>
          {SCAFFOLDED.map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-center gap-2 px-3 py-2 text-sm text-[#8A8782] cursor-not-allowed">
              <Icon className="w-4 h-4" strokeWidth={1.5} /> {label}
              <Lock className="w-3 h-3 ml-auto" strokeWidth={1.5} />
            </div>
          ))}
        </nav>
      </aside>

      <main className="flex-1 overflow-auto">
        <Routes>
          <Route index element={<Overview />} />
          <Route path="products" element={<ProductsPanel />} />
          <Route path="orders" element={<OrdersPanel />} />
          <Route path="customers" element={<Placeholder title="Customers" subtitle="Track your customer base." />} />
          <Route path="coupons" element={<Placeholder title="Coupons" subtitle="Build promo codes and limited-time offers." />} />
          <Route path="analytics" element={<Placeholder title="Analytics" subtitle="Deep dive into revenue & conversion." />} />
          <Route path="reviews" element={<Placeholder title="Reviews" subtitle="Reply to verified customer reviews." />} />
          <Route path="messages" element={<Placeholder title="Messages" subtitle="Inbox for customer-to-merchant messages." />} />
          <Route path="settings" element={<StoreSettings />} />
        </Routes>
      </main>
    </div>
  );
}

function NavItem({ to, icon: Icon, label, end }) {
  return (
    <NavLink to={to} end={end} className={({ isActive }) =>
      `flex items-center gap-2 px-3 py-2 rounded-sm text-sm ${isActive ? "bg-[#F2E8E9] text-[#702A32]" : "text-[#5C5A56] hover:bg-[#EFECE6]"}`
    } data-testid={`merchant-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <Icon className="w-4 h-4" strokeWidth={1.5} /> {label}
    </NavLink>
  );
}

function Overview() {
  const [metrics, setMetrics] = useState(null);
  const [store, setStore] = useState(null);
  useEffect(() => {
    api.get("/merchants/me/metrics").then(({ data }) => setMetrics(data)).catch(() => {});
    api.get("/merchants/me/store").then(({ data }) => setStore(data)).catch(() => {});
  }, []);
  return (
    <div className="p-6 md:p-10" data-testid="merchant-overview">
      <div className="overline mb-2">Dashboard</div>
      <h1 className="font-heading text-3xl md:text-4xl tracking-tight">{store?.name || "Your store"}</h1>
      {store && <Link to={`/store/${store.slug}`} className="text-sm text-[#702A32] hover:underline">View public storefront →</Link>}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
        <Metric label="Revenue" value={formatCAD(metrics?.revenue || 0)} testid="metric-revenue" />
        <Metric label="Orders" value={metrics?.orders || 0} testid="metric-orders" />
        <Metric label="Customers" value={metrics?.customers || 0} testid="metric-customers" />
        <Metric label="Conversion" value={`${metrics?.conversion || 0}%`} testid="metric-conversion" />
        <Metric label="Products" value={metrics?.products || 0} />
        <Metric label="Reviews" value={metrics?.reviews || 0} />
        <Metric label="Store visits" value={metrics?.store_visits || 0} />
        <Metric label="Billing model" value={store?.billing_model || "—"} />
      </div>

      <div className="mt-10 grid md:grid-cols-3 gap-6">
        <div className="surface p-6 md:col-span-2">
          <h3 className="font-heading text-xl mb-4">Quick links</h3>
          <ul className="space-y-2 text-sm">
            <li><Link to="/merchant/products" className="text-[#702A32] hover:underline">+ Add a new product</Link></li>
            <li><Link to="/merchant/orders" className="text-[#702A32] hover:underline">Review pending orders</Link></li>
            <li><Link to="/merchant/settings" className="text-[#702A32] hover:underline">Update store branding & policies</Link></li>
          </ul>
        </div>
        <div className="surface p-6">
          <div className="overline mb-2">Billing</div>
          <div className="font-heading text-2xl">{store?.billing_model}</div>
          <p className="text-sm text-[#5C5A56] mt-2">
            {store?.billing_model === "COMMISSION" && `${store?.commission_pct}% commission per sale`}
            {store?.billing_model === "SUBSCRIPTION" && `${formatCAD(store?.subscription_fee)}/month flat`}
            {store?.billing_model === "MARKUP" && `${store?.markup_pct}% markup added at checkout`}
            {store?.billing_model === "HYBRID" && `${store?.commission_pct}% commission + ${formatCAD(store?.subscription_fee)}/month`}
          </p>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, testid }) {
  return (
    <div className="surface p-5" data-testid={testid}>
      <div className="overline">{label}</div>
      <div className="font-heading text-3xl mt-2">{value}</div>
    </div>
  );
}

function ProductsPanel() {
  const [products, setProducts] = useState([]);
  const [store, setStore] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", price: "", description: "", featured_image: "", product_type: "STANDARD" });

  const load = async () => {
    const { data: s } = await api.get("/merchants/me/store");
    setStore(s);
    const { data: items } = await api.get(`/merchants/${s.merchant_id}/products`);
    setProducts(items);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    await api.post("/products", {
      ...form,
      price: parseFloat(form.price),
      images: form.featured_image ? [form.featured_image] : [],
    });
    setShowNew(false);
    setForm({ name: "", price: "", description: "", featured_image: "", product_type: "STANDARD" });
    load();
  };

  return (
    <div className="p-6 md:p-10" data-testid="merchant-products">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-3xl">Products</h1>
        <button onClick={() => setShowNew(true)} className="btn-primary" data-testid="merchant-add-product">+ Add product</button>
      </div>

      {showNew && (
        <form onSubmit={create} className="surface p-6 mb-6 space-y-4" data-testid="merchant-new-product-form">
          <h3 className="font-heading text-xl">New product</h3>
          <input className="input-field" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          <textarea className="input-field" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <div className="grid md:grid-cols-2 gap-4">
            <input className="input-field" type="number" step="0.01" placeholder="Price (CAD)" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required />
            <select className="input-field" value={form.product_type} onChange={(e) => setForm({ ...form, product_type: e.target.value })}>
              <option value="STANDARD">Standard</option>
              <option value="POD">Print-on-Demand</option>
              <option value="CUSTOM">Customizable text</option>
            </select>
          </div>
          <input className="input-field" placeholder="Featured image URL" value={form.featured_image} onChange={(e) => setForm({ ...form, featured_image: e.target.value })} />
          <div className="flex gap-2">
            <button type="submit" className="btn-primary">Create</button>
            <button type="button" onClick={() => setShowNew(false)} className="btn-secondary">Cancel</button>
          </div>
        </form>
      )}

      <div className="surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-[#E5E3DD] text-left">
            <tr className="text-xs uppercase tracking-wider text-[#5C5A56]">
              <th className="p-4">Product</th><th className="p-4">Type</th><th className="p-4">Price</th><th className="p-4">Inventory</th><th className="p-4">Rating</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.product_id} className="border-b border-[#E5E3DD] hover:bg-[#FDFBF7]">
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <img src={p.featured_image} alt="" className="w-10 h-10 object-cover" />
                    <span className="font-medium">{p.name}</span>
                  </div>
                </td>
                <td className="p-4"><span className="tag-pill bg-[#EFECE6] text-[#5C5A56]">{p.product_type}</span></td>
                <td className="p-4 font-mono">{formatCAD(p.price)}</td>
                <td className="p-4">{p.inventory}</td>
                <td className="p-4">{p.rating?.toFixed(1)} ({p.review_count})</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function OrdersPanel() {
  const [orders, setOrders] = useState([]);
  useEffect(() => { api.get("/merchants/me/orders").then(({ data }) => setOrders(data)); }, []);
  return (
    <div className="p-6 md:p-10" data-testid="merchant-orders">
      <h1 className="font-heading text-3xl mb-6">Orders</h1>
      <div className="surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-[#E5E3DD] text-xs uppercase tracking-wider text-[#5C5A56] text-left">
            <tr><th className="p-4">Order</th><th className="p-4">Customer</th><th className="p-4">Status</th><th className="p-4">Total</th><th className="p-4">Date</th></tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.order_id} className="border-b border-[#E5E3DD]">
                <td className="p-4 font-mono">{o.order_id.slice(-8)}</td>
                <td className="p-4">{o.customer_email}</td>
                <td className="p-4"><span className={`tag-pill ${STATUS_COLOR[o.status]}`}>{o.status}</span></td>
                <td className="p-4 font-mono">{formatCAD(o.total)}</td>
                <td className="p-4">{new Date(o.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StoreSettings() {
  const [store, setStore] = useState(null);
  useEffect(() => { api.get("/merchants/me/store").then(({ data }) => setStore(data)); }, []);
  if (!store) return null;
  return (
    <div className="p-6 md:p-10 max-w-3xl" data-testid="merchant-settings">
      <h1 className="font-heading text-3xl mb-6">Store settings</h1>
      <div className="surface p-6 space-y-4">
        <Row k="Name" v={store.name} />
        <Row k="Slug" v={`/store/${store.slug}`} />
        <Row k="Types" v={store.types.join(", ")} />
        <Row k="Billing model" v={store.billing_model} />
        <Row k="Location" v={store.location} />
        <Row k="Custom domain" v={store.custom_domain || "Not configured (coming soon)"} />
      </div>
    </div>
  );
}
function Row({ k, v }) {
  return <div className="flex justify-between py-2 border-b border-[#E5E3DD] last:border-0"><span className="text-sm text-[#5C5A56]">{k}</span><span className="text-sm font-medium">{v}</span></div>;
}

function Placeholder({ title, subtitle }) {
  return (
    <div className="p-6 md:p-10">
      <h1 className="font-heading text-3xl">{title}</h1>
      <p className="text-[#5C5A56] mt-2">{subtitle}</p>
      <div className="surface p-12 mt-8 text-center text-[#8A8782]">
        <Box className="w-10 h-10 mx-auto mb-3" strokeWidth={1.5} />
        This module is scaffolded. Full functionality is on the roadmap.
      </div>
    </div>
  );
}
